import { useCookie } from 'nuxt/app'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
// Using useUUID composable for consistent UUID generation
import { calculateCartItemTotal as calculateTotal } from './commerce.calculations'
// Removed getWithExpiry, setWithExpiry - now using Pinia persistence
import type { Customer, Product, CartItem, NormalizedCartItem, CartItemModifier, ModifierSelection, CustomItemInput } from '~/types/commerce'
import type { PaymentMethod } from '~/stores/admin/paymentMethods'
import type { Discount } from '~/stores/admin/discounts'
// Import component only when needed to avoid test issues
import PublicStoreCombosModal from '~/components/PublicStore/Combos/Modal.vue'
import { dialog as dialogStyle } from '~/utils/primeStyles/dialog'

interface Extra {
  price?: number
}

interface ComboItem {
  selected: boolean
  price_variation?: number
  extras?: Extra[]
}

// Local type to avoid conflicts with commerce.zod.ts
type ComboItemType = {
  group_id: number
  group_name: string
  options: Array<Record<string, unknown>>
}

// Product Modifier mapping interfaces
interface ProductModifier {
  id: string
  name: string
  is_multiple: boolean
  is_required: boolean
  options: ProductOption[]
  default_option: ProductOption[]
}

interface ProductOption {
  id: string
  name: string
  is_default: boolean
  price_change: number
}

/**
 * Maps a Product Modifier to the unified CartItemModifier structure
 */
function mapProductModifierToCartModifier(productModifier: ProductModifier): CartItemModifier {
  // Get default selections
  const defaultSelections = productModifier.default_option
    || (productModifier.options?.filter(option => option.is_default) ?? [])

  let selected: ModifierSelection | ModifierSelection[] | null = null

  if (defaultSelections.length > 0) {
    const mappedSelections = defaultSelections.map(option => ({
      id: option.id,
      name: option.name,
      price_modifier: option.price_change,
      quantity: 1,
    }))

    selected = productModifier.is_multiple ? mappedSelections : mappedSelections[0]
  }

  return {
    group_id: productModifier.id,
    group_name: productModifier.name,
    required: productModifier.is_required,
    multiple: productModifier.is_multiple,
    options: productModifier.options, // Include options for template
    selected,
  }
}

/**
 * Maps an array of Product Modifiers to CartItemModifier array
 */
function mapProductModifiersToCartModifiers(productModifiers: ProductModifier[]): CartItemModifier[] {
  if (!Array.isArray(productModifiers) || productModifiers.length === 0) {
    return []
  }

  return productModifiers.map(mapProductModifierToCartModifier)
}

// Use the imported calculateCartItemTotal function from commerce.calculations.ts
const calculateCartItemTotal = calculateTotal

/**
 * Commerce store to manage products, cart, and checkout flow
 */
export const useCommerceStore = defineStore('commerce', () => {
  const log = useLogger('store:commerce')
  const { get } = useAuthenticatedFetch()

  // Use business store as single source of truth
  const businessStore = useBusinessStore()
  const { businessId, businessInfo } = storeToRefs(businessStore)
  const paymentMethodsStore = usePaymentMethodsStore()
  const discountsStore = useDiscountsStore()

  // Store state
  const products = ref<Product[]>([])
  const combos = ref<any[]>([])
  const categories = ref<Record<string, unknown>[]>([])
  const cart = ref<CartItem[]>([])
  const openCart = ref<boolean>(false)
  const productModal = ref<boolean>(false)
  const comboModal = ref<boolean>(false)
  const productStaged = ref<Product | null>(null)
  const discounts = ref<Discount[]>([])
  const selectedDiscount = ref<Discount | null>(null)
  const selectedPaymentMethod = ref<PaymentMethod | null>(null)
  const editButtons = ref<boolean>(true)
  const compactViewEnabled = ref<boolean>(true) // Default to compact view like restaurant mode
  const isUpdatingOrder = ref<boolean>(false)

  // Counter mode cash calculation
  const cashReceived = ref<number | null>(null)

  // Combo related state
  const comboStaged = ref<Record<string, unknown>>({})
  const comboCustomized = ref<boolean>(false)
  const comboCustomOptions = ref<Record<string, unknown> | null>(null)

  // Order editing state
  const editingOrderId = ref<string | null>(null)
  const isEditingOrder = ref<boolean>(false)

  // Category filtering state with cookie persistence
  const selectedCategoryCookie = useCookie<string | null>('selectedCategory', {
    default: () => null,
    maxAge: 60 * 60 * 24 * 7, // 7 days
  })
  const selectedCategoryId = ref<string | null>(selectedCategoryCookie.value)

  // Search filtering state
  const searchTerm = ref<string>('')

  // Initial customer details state
  const initialDetailsState = {
    email: undefined,
    name: '',
    phone: undefined,
    postal_code: undefined,
    address_line_1: undefined,
    address_line_2: undefined,
    city: undefined,
    country: undefined,
    delivery: false,
    comments: undefined,
  }

  // Deep clone utility function
  function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj))
  }

  const details = ref<Customer>(deepClone(initialDetailsState))

  // ========== Computed properties ==========

  const itemsInCart = computed((): number => {
    if (!Array.isArray(cart.value) || cart.value.length === 0) {
      return 0
    }

    return cart.value.reduce((total, item) => total + item.quantity, 0)
  })

  const cartTotal = computed((): string => {
    if (!Array.isArray(cart.value) || cart.value.length === 0) {
      return '0.00'
    }

    const total = cart.value.reduce((accumulator, item) => {
      // Use the pre-calculated total if available, but recalculate for CUSTOM_COMBO to ensure modifiers are included
      if (item.total && typeof item.total === 'number' && item.type !== 'CUSTOM_COMBO') {
        return accumulator + item.total
      }

      // Use the imported calculateCartItemTotal function for consistency
      return accumulator + calculateCartItemTotal(item)
    }, 0)

    const percentage = selectedDiscount.value?.percentage || 0
    const paymentMethodSurcharge = selectedPaymentMethod.value?.surcharge_percentage || 0

    let finalTotal = total

    // Apply discount if available
    if (percentage > 0) {
      const discountAmount = total * (percentage / 100)
      finalTotal = total - discountAmount
    }

    // Apply payment method surcharge if not 0
    if (paymentMethodSurcharge > 0) {
      const surchargeAmount = finalTotal * (paymentMethodSurcharge / 100)
      finalTotal += surchargeAmount
    }

    return Math.round(finalTotal).toString()
  })

  const paymentMethodSurchargePercentage = computed((): number => {
    const value = selectedPaymentMethod.value?.surcharge_percentage || 0
    return Math.round(value * 100) / 100
  })

  const calculatedChange = computed((): number | null => {
    if (cashReceived.value === null || cashReceived.value === 0) {
      return null
    }
    const change = cashReceived.value - Number(cartTotal.value)
    return change >= 0 ? change : 0
  })

  const hasClientDetails = computed((): boolean => {
    return JSON.stringify(details.value) !== JSON.stringify(initialDetailsState)
  })

  const stageProductInCart = computed((): boolean => {
    if (!productStaged.value || !productStaged.value.cart_uuid) {
      return false
    }

    const found = cart.value.find(item => item.cart_uuid === productStaged.value?.cart_uuid)
    return (typeof found !== 'undefined')
  })

  const productStagedTotal = computed((): number => {
    if (!productStaged.value) {
      return 0
    }

    // Create a cart item representation to use the existing calculation logic
    const cartItem: CartItem = {
      cart_uuid: productStaged.value.cart_uuid || '',
      name: productStaged.value.name,
      type: 'PRODUCT',
      base_price: productStaged.value.price || 0,
      quantity: productStaged.value.quantity || 1,
      total: 0, // Will be calculated
      modifiers: productStaged.value.modifiers || [],
      items: [],
    }

    return calculateCartItemTotal(cartItem)
  })

  const stageComboInCart = computed((): boolean => {
    if (!comboStaged.value || !comboStaged.value.cart_uuid) {
      return false
    }

    const found = cart.value.find(item => item.cart_uuid === comboStaged.value.cart_uuid)
    return (typeof found !== 'undefined')
  })

  const comboStagedTotal = computed((): number => {
    if (!comboStaged.value?.quantity || !comboStaged.value?.price) {
      return 0
    }

    const baseTotal = comboStaged.value.quantity * comboStaged.value.price

    // Add extras cost if available (for custom combos)

    comboStaged.value.total = baseTotal

    return baseTotal
  })

  const headerTitle = computed((): string => {
    const name = comboStaged.value?.name ?? null
    const quantity = comboStaged.value?.quantity ?? null

    if (!name || !quantity) {
      return 'Combo'
    }

    return `${quantity} x Combo ${name}`
  })

  // Category filtering computed properties
  const filteredProducts = computed(() => {
    let filtered = products.value

    // Apply search filter
    if (searchTerm.value.trim()) {
      const term = searchTerm.value.toLowerCase()
      filtered = filtered.filter((product: any) =>
        product.name.toLowerCase().includes(term),
      )
    }

    // Apply category filter
    if (selectedCategoryId.value && Array.isArray(filtered)) {
      if (selectedCategoryId.value === 'miscellaneous') {
        filtered = filtered.filter((product: any) =>
          !product.categories || product.categories.length === 0,
        )
      }
      else {
        filtered = filtered.filter((product: any) =>
          product.categories && Array.isArray(product.categories)
          && product.categories.some((category: any) => category.id === selectedCategoryId.value),
        )
      }
    }

    return filtered.sort((a: any, b: any) => (a.name as string).localeCompare(b.name as string))
  })

  const availableCategories = computed(() => {
    const nuxtApp = useNuxtApp()
    const { t } = nuxtApp.$i18n

    if (!Array.isArray(categories.value) || !Array.isArray(products.value)) {
      return []
    }

    // Filter categories that have products
    const categoriesWithProducts = categories.value.filter((category: any) =>
      products.value.some((product: any) =>
        product.categories && Array.isArray(product.categories)
        && product.categories.some((c: any) => c.id === category.id),
      ),
    )

    // Add "Miscellaneous" if uncategorized products exist
    const uncategorizedProducts = products.value.filter((product: any) =>
      !product.categories || product.categories.length === 0,
    )

    if (uncategorizedProducts.length > 0) {
      categoriesWithProducts.push({
        id: 'miscellaneous',
        name: t('Miscellaneous'),
        product_count: uncategorizedProducts.length,
      })
    }

    return categoriesWithProducts.sort((a, b) => (a.name as string).localeCompare(b.name as string))
  })

  // ========== Local Storage Functions ==========

  function persistDetailsInLocalStorage(): void {
    // Details are now persisted automatically by Pinia persistence
    // This function is kept for backward compatibility but does nothing
    // since details are in the persist paths array
  }

  function persistPreferencesInLocalStorage(): void {
    // Preferences are now persisted automatically by Pinia persistence
    // This function is kept for backward compatibility but does nothing
    // since editButtons are in the persist paths array
  }

  function readCartFromLocalStorage(): void {
    // Cart, details, and preferences are now handled by Pinia persistence
    // Store disposal handles business switching
    // Business ID is managed by business store, no need to set it here
  }

  function persistCartInLocalStorage(): void {
    // Cart is now persisted automatically by Pinia persistence
    // This function is kept for backward compatibility but does nothing
    // since cart is in the persist paths array
  }

  function reset(): void {
    // Reset all store state to initial values
    businessStore.clearBusinessData()
    products.value = []
    combos.value = []
    categories.value = []
    cart.value = []
    openCart.value = false
    productModal.value = false
    comboModal.value = false
    productStaged.value = null
    discounts.value = []
    selectedDiscount.value = null
    selectedPaymentMethod.value = null
    isUpdatingOrder.value = false

    // Combo related state
    comboStaged.value = {}
    comboCustomized.value = false
    comboCustomOptions.value = null

    // Order editing state
    editingOrderId.value = null
    isEditingOrder.value = false

    // Category filtering state
    selectedCategoryId.value = null

    // Search filtering state
    searchTerm.value = ''

    // Customer details (reset to initial state)
    details.value = deepClone(initialDetailsState)

    // Keep user preferences
    // editButtons.value - keep this as it's a user preference

    // Data management state
    isLoadingData.value = false

    log.info('✅ Commerce store reset to initial state')
  }

  function clearDetails(): void {
    // Details clearing is now handled automatically by store reset
    details.value = deepClone(initialDetailsState)
  }

  function clearCart(): void {
    cart.value = []
    selectedDiscount.value = null
    resetCashCalculation()
    // Cart clearing is now handled automatically by Pinia persistence
  }

  function setCashReceived(amount: number): void {
    cashReceived.value = amount
  }

  function resetCashCalculation(): void {
    cashReceived.value = null
  }

  // ========== Product Functions ==========

  // DEPRECATED FUNCTIONS REMOVED:
  // - getDefaultOption (legacy product options)
  // - setProductOptionSelected (legacy product options)

  function stageProduct(product: Product): void {
    const clonedProduct = deepClone(product)
    clonedProduct.quantity = 1
    clonedProduct.custom = []
    clonedProduct.type = 'PRODUCT'
    clonedProduct.notes = ''
    const { generate } = useUUID()
    clonedProduct.cart_uuid = generate()
    // Don't set total manually - let productStagedTotal computed property handle it

    // Map modifiers to the unified structure for template display
    clonedProduct.modifiers = product.modifiers
      ? mapProductModifiersToCartModifiers(product.modifiers)
      : []

    // No more optionSelected - fully deprecated
    clonedProduct.optionSelected = null

    productStaged.value = clonedProduct
  }

  function resetProductStaged(): void {
    productStaged.value = null
  }

  function addProductToCart(product: Product): void {
    // Use the staged product's modifiers which include user selections
    // Clean any reactive wrappers that might cause storage issues
    const modifiers: CartItemModifier[] = product.modifiers
      ? JSON.parse(JSON.stringify(product.modifiers))
      : []

    const normalizedProduct: CartItem = {
      cart_uuid: product.cart_uuid || '',
      name: product.name,
      type: 'PRODUCT',
      base_price: product.price,
      quantity: product.quantity || 1,
      total: 0, // Will be calculated below
      // Store modifiers directly on the cart item (new unified structure)
      modifiers: modifiers,
      notes: product.notes,
      items: [{
        id: 'product_item',
        product_id: product.id,
        product_name: product.name,
        quantity: product.quantity || 1,
        base_price: product.price,
        group: null,
        // modifiers: REMOVED - moved to cart item level for consistency with combos
      }],
      custom: product.custom, // TODO: This will also be migrated to modifiers
      // optionSelected: REMOVED - deprecated
      // price: REMOVED - calculated from base_price + modifiers + extras
    }

    // Calculate the correct total including modifiers
    normalizedProduct.total = calculateCartItemTotal(normalizedProduct)

    cart.value.push(normalizedProduct)
    persistCartInLocalStorage()
    resetProductStaged()
  }

  function editProductFromCart(cartUuid: string): void {
    const item = cart.value.find(item => item.cart_uuid === cartUuid)

    if (item && item.items && item.items.length > 0) {
      const productItem = item.items[0] // Get the first (and typically only) product item

      // Transform cart item structure to match fresh product structure for staging
      const productForStaging = {
        // Product identification
        id: productItem.product_id,
        name: item.name,

        // Pricing - use base_price from cart item
        price: item.base_price,
        base_price: item.base_price,

        // Cart metadata for updating
        cart_uuid: item.cart_uuid,
        quantity: item.quantity,
        total: item.total,

        // Product metadata
        type: item.type || 'PRODUCT',
        custom: item.custom || [],

        // Use modifiers from new direct structure, fallback to legacy nested structure for backward compatibility
        modifiers: item.modifiers || productItem.modifiers || [],

        // Special instructions
        notes: item.notes,

        // Deprecated fields for compatibility
        optionSelected: null,
      }

      productStaged.value = productForStaging
      productModal.value = true
    }
    else {
      log.error('Cart item not found or has invalid structure:', cartUuid)
    }
  }

  function updateProductInCart(product: Product): void {
    if (!product.cart_uuid) {
      log.error('Product missing cart_uuid for update')
      return
    }

    // Show updating message
    isUpdatingOrder.value = true

    const index = cart.value.findIndex(item => item.cart_uuid === product.cart_uuid)

    if (index !== -1) {
      // Use the staged product's modifiers which include user selections
      // Clean any reactive wrappers that might cause storage issues
      const modifiers: CartItemModifier[] = product.modifiers
        ? JSON.parse(JSON.stringify(product.modifiers))
        : []

      const updatedProduct: CartItem = {
        cart_uuid: product.cart_uuid,
        name: product.name,
        type: 'PRODUCT',
        base_price: product.price,
        quantity: product.quantity || 1,
        total: product.total || product.price,
        // Store modifiers directly on the cart item (new unified structure)
        modifiers: modifiers,
        notes: product.notes,
        items: [{
          id: 'product_item',
          product_id: product.id,
          product_name: product.name,
          quantity: product.quantity || 1,
          base_price: product.price,
          group: null,
          // modifiers: REMOVED - moved to cart item level for consistency with combos
        }],
        custom: product.custom,
      }

      // Calculate the total using the unified function
      updatedProduct.total = calculateCartItemTotal(updatedProduct)

      // Replace the existing item
      cart.value[index] = updatedProduct
      persistCartInLocalStorage()
      resetProductStaged()

      useSuccessToast('Product updated successfully!')

      // Hide updating message after a short delay to ensure user sees feedback
      setTimeout(() => {
        isUpdatingOrder.value = false
      }, 500)
    }
    else {
      useErrorToast('Product not found in cart')
      isUpdatingOrder.value = false
    }
  }

  function increaseProductQuantity(product: CartItem): void {
    product.quantity += 1
    product.total = calculateCartItemTotal(product)
    persistCartInLocalStorage()
  }

  function decreaseProductQuantity(product: CartItem): void {
    if (product.quantity === 1) {
      removeProductFromCart(product.cart_uuid)
      return
    }

    product.quantity -= 1
    product.total = calculateCartItemTotal(product)
    persistCartInLocalStorage()
  }

  // Tile +/- only touch plain PRODUCT rows: combos and custom items that contain
  // the product keep their own quantity.
  function addOneToCart(product: Product): void {
    const productInCart = cart.value.find(item => item.type === 'PRODUCT' && item.items[0]?.product_id === product.id)

    if (productInCart) {
      productInCart.quantity += 1
      productInCart.total = productInCart.quantity * productInCart.base_price

      if (productStaged.value && productStaged.value.id === product.id) {
        productStaged.value = productInCart as any || productStaged.value
      }
    }
    else if (productStaged.value) {
      productStaged.value.quantity = (productStaged.value.quantity || 1) + 1
      // The total is now computed via productStagedTotal, no need to manually set it
      addProductToCart(productStaged.value)
    }

    persistCartInLocalStorage()
  }

  /**
   * Quick-add a product with no modifiers directly to cart.
   * If the product already exists in the cart, increments its quantity.
   * Returns the cart_uuid of the added/updated item.
   */
  function quickAddToCart(product: Product): string {
    const existingItem = cart.value.find(item =>
      item.type === 'PRODUCT'
      && item.items?.[0]?.product_id === product.id
      && (!item.modifiers || item.modifiers.length === 0),
    )

    if (existingItem) {
      existingItem.quantity += 1
      existingItem.total = calculateCartItemTotal(existingItem)
      persistCartInLocalStorage()
      return existingItem.cart_uuid
    }

    // Stage and add as new cart item
    stageProduct(product)
    if (productStaged.value) {
      addProductToCart(productStaged.value)
    }
    // Find the just-added item to return its cart_uuid
    const addedItem = cart.value[cart.value.length - 1]
    return addedItem?.cart_uuid || ''
  }

  function removeOneFromCart(product: Product): void {
    const productClone = deepClone(product)
    const productInCart = cart.value.find(item => item.type === 'PRODUCT' && item.items[0]?.product_id === productClone.id)

    if (productInCart && productInCart.quantity === 1) {
      const productIndex = cart.value.findIndex(item => item.type === 'PRODUCT' && item.items[0]?.product_id === productClone.id)
      cart.value.splice(productIndex, 1)

      if (productStaged.value && productStaged.value.id === productClone.id) {
        productStaged.value.quantity = 0
        // The total is now computed via productStagedTotal, no need to manually set it
      }
    }

    if (productInCart && productInCart.quantity > 1) {
      productInCart.quantity -= 1
      productInCart.total = productInCart.quantity * productInCart.base_price
    }

    persistCartInLocalStorage()
  }

  function removeProductFromCart(cartUuid: string): void {
    const index = cart.value.findIndex(item => item.cart_uuid === cartUuid)

    if (index !== -1) {
      cart.value.splice(index, 1)
      persistCartInLocalStorage()
    }
  }

  // ========== Combo Functions ==========

  function prepareComboForCart(combo: any): CartItem {
    const comboPrep = {
      cart_uuid: combo?.cart_uuid,
      name: combo?.name,
      base_price: combo?.price ?? 0,
      quantity: combo?.quantity ?? 1,
      items: prepareItems(combo?.items ?? []),
      total: combo?.total ?? 0,
      type: 'COMBO' as const,
    }

    function prepareItems(items: any[]): NormalizedCartItem[] {
      // Input validation
      if (!Array.isArray(items)) {
        log.error('Expected items to be an array')
        return []
      }

      if (items.length === 0) return []

      const prepared = items.map((item, index) => {
        if (!item) {
          log.warn('Encountered null or undefined item')
          return null
        }

        // Validate item structure
        if (!item.options || !Array.isArray(item.options)) {
          log.warn(`Item missing options array: ${JSON.stringify(item)}`)
          return {
            id: `item_${index}`,
            product_id: 0,
            product_name: '',
            quantity: 0,
            base_price: 0,
            group: {
              id: item.group_id || 0,
              name: item.group_name || '',
              required: true,
            },
            selected_modifiers: {},
            selected_option: null,
          }
        }

        const defaultOption = getDefaultOption(item.options)
        if (!defaultOption) {
          log.warn(`No valid option found for item: ${JSON.stringify(item)}`)
          return {
            id: `item_${index}`,
            product_id: 0,
            product_name: '',
            quantity: 0,
            base_price: 0,
            group: {
              id: item.group_id || 0,
              name: item.group_name || '',
              required: true,
            },
            selected_modifiers: {},
            selected_option: null,
          }
        }

        const cartItem = {
          id: `item_${index}`,
          product_id: defaultOption.product_id || 0,
          product_name: defaultOption.product_name || '',
          quantity: defaultOption.quantity || 0,
          base_price: 0,
          group: {
            id: item.group_id || 0,
            name: item.group_name || '',
            required: true,
          },
          selected_modifiers: {},
        }

        // Set default modifiers if product has modifiers
        if (defaultOption.product_modifiers?.length > 0) {
          cartItem.selected_modifiers = setDefaultModifiersForProduct(defaultOption.product_modifiers, {})
        }

        return cartItem
      }).filter(Boolean) as NormalizedCartItem[] // Remove any null entries

      return prepared
    }

    function getDefaultOption(options: any[]) {
      if (!Array.isArray(options) || options.length === 0) {
        return null
      }

      // Find the first option with default=true
      const defaultOption = options.find(item => item && item.default === true)

      // Return the default option if found, otherwise return the first option
      // Also check that the first option is valid
      return defaultOption || (options[0] && typeof options[0] === 'object' ? options[0] : null)
    }

    return deepClone(comboPrep)
  }

  function addComboToCart(combo: any): void {
    const comboClone = prepareComboForCart(combo)
    cart.value.push(comboClone)
    persistCartInLocalStorage()
  }

  function addCustomComboToCart(combo: any): void {
    const normalizedCombo: CartItem = {
      cart_uuid: combo.cart_uuid,
      name: combo.name,
      type: combo.type || 'CUSTOM_COMBO',
      base_price: combo.price || 0,
      quantity: combo.quantity || 1,
      // Don't set total - let calculateCartItemTotal handle it
      combo_id: combo.combo_id, // Preserve combo ID for editing
      items: combo.items
        ? combo.items.map((item: any, index: number): NormalizedCartItem => ({
            id: `item_${index}`,
            product_id: item.product_id || 0,
            product_name: item.product_name || '',
            quantity: item.quantity || 1,
            base_price: 0,
            price_variation: item.price_variation || 0,
            group: item.group_id
              ? {
                  id: item.group_id,
                  name: item.group_name || '',
                  required: true,
                }
              : null,
            selected_modifiers: item.selected_modifiers || {},
          }))
        : [],
    }

    // Calculate the correct total
    normalizedCombo.total = calculateCartItemTotal(normalizedCombo)

    cart.value.push(normalizedCombo)
    persistCartInLocalStorage()
  }

  function addCustomItemToCart(input: CustomItemInput): void {
    const linked = input.items ?? []
    // With catalog products it is an ad-hoc CUSTOM_COMBO: the price stays manual
    // (children carry no price) and every consumer already deducts stock per child.
    const customItem: CartItem = {
      cart_uuid: crypto.randomUUID(),
      name: input.name,
      type: linked.length ? 'CUSTOM_COMBO' : 'PRODUCT',
      is_custom_item: true,
      base_price: input.price,
      quantity: input.quantity,
      total: input.price * input.quantity,
      notes: input.notes,
      items: linked.map(i => ({
        id: crypto.randomUUID(),
        product_id: i.product_id,
        product_name: i.product_name,
        quantity: i.quantity,
        base_price: 0,
        price_variation: 0,
        group: null,
        modifiers: [],
      })),
      modifiers: [],
    }

    cart.value.push(customItem)
    persistCartInLocalStorage()
  }

  async function editComboFromCart(cartUuid: string): Promise<void> {
    const item = cart.value.find(item => item.cart_uuid === cartUuid)

    if (item?.is_custom_item) {
      useErrorToast('Remove the item and add it again to change it')
      return
    }

    if (item) {
      let originalCombo = null

      // Try to find combo by combo_id first
      if (item.combo_id) {
        originalCombo = combos.value.find(combo => combo.id === item.combo_id)
      }

      // Fallback: try to find combo by name for legacy cart items
      if (!originalCombo && item.name) {
        originalCombo = combos.value.find(combo => combo.name === item.name)
        log.info('Found combo by name fallback:', originalCombo?.id)
      }

      if (originalCombo) {
        // Set up fresh combo state using existing working logic
        // The combo from store already has the correct v2 structure
        openComboModal(originalCombo)

        // Preserve the quantity, cart UUID, and base_price for updating
        comboStaged.value.quantity = item.quantity // Keep the user's desired quantity
        comboStaged.value.cart_uuid = cartUuid // For update detection
        comboStaged.value.base_price = item.base_price // Preserve the custom combo base price

        // Enable customization mode using existing logic
        await customizeCombo()

        // Open the combo dialog using existing system
        const { $dialog } = useNuxtApp()

        $dialog.open(PublicStoreCombosModal, {
          props: {
            header: `${item.quantity} x ${item.name}`,
            ...useDynamicDialogSettings,
            pt: dialogStyle,
            modal: true,
            position: 'top',
            style: { width: '95vw' },
            breakpoints: {
              '1199px': '90vw',
              '575px': '90vw',
            },
          },
          onClose: () => {
            resetComboData()
          },
        })
      }
      else {
        log.warn('Combo not found for editing:', {
          combo_id: item.combo_id,
          name: item.name,
          available_combos: combos.value.map(c => ({ id: c.id, name: c.name })),
        })
        useErrorToast('This combo is not available for editing.')
      }
    }
  }

  // Helper function to detect if a combo has custom selections
  function _hasCustomSelections(item: any): boolean {
    if (!item.items || !Array.isArray(item.items)) return false

    // Check if any item has custom selections
    return item.items.some((comboItem: any) => {
      // Check for unified modifiers (new system)
      if (comboItem.selected_modifiers && Object.keys(comboItem.selected_modifiers).length > 0) {
        return true
      }

      return false
    })
  }

  function increaseComboQuantityStaged(): void {
    if (!comboStaged.value) return

    comboStaged.value.quantity += 1
    // Selected extras are preserved automatically in comboStaged.value.items
    // The comboStagedTotal computed property will handle the total calculation including extras
    persistCartInLocalStorage()
  }

  function decreaseComboQuantityStaged(): void {
    if (!comboStaged.value || comboStaged.value.quantity <= 1) return

    comboStaged.value.quantity -= 1
    // Selected extras are preserved automatically in comboStaged.value.items
    // The comboStagedTotal computed property will handle the total calculation including extras
    persistCartInLocalStorage()
  }

  function increaseComboQuantityCart(combo: CartItem): void {
    combo.quantity += 1
    combo.total = combo.quantity * combo.base_price
    // increaseDefaults(combo)
    persistCartInLocalStorage()
  }

  function decreaseComboQuantityCart(combo: CartItem): void {
    if (combo.quantity === 1) {
      removeProductFromCart(combo.cart_uuid)
      return
    }

    combo.quantity -= 1
    combo.total = combo.quantity * combo.base_price
    // decreaseDefaults(combo)
    persistCartInLocalStorage()
  }

  // function increaseDefaults(combo: ComboType): void {
  //   if (!combo.defaults || !Array.isArray(combo.defaults)) return

  //   combo.defaults.forEach((option: any) => {
  //     if (typeof option.quantity === 'number') {
  //       option.quantity += 1
  //     }
  //   })
  // }

  // function decreaseDefaults(combo: ComboType): void {
  //   if (!combo.defaults || !Array.isArray(combo.defaults)) return

  //   combo.defaults.forEach((option: any) => {
  //     if (typeof option.quantity === 'number') {
  //       option.quantity -= 1
  //     }
  //   })
  // }

  // Use the imported calculateCartItemTotal function instead of duplicate logic
  const calculateComboTotal = calculateCartItemTotal

  function increaseCustomComboQuantityCart(combo: CartItem): void {
    combo.quantity += 1

    // Use the combo-specific calculation function
    combo.total = calculateComboTotal(combo)

    persistCartInLocalStorage()
  }

  function decreaseCustomComboQuantityCart(combo: CartItem): void {
    if (combo.quantity === 1) {
      removeProductFromCart(combo.cart_uuid)
      return
    }

    combo.quantity -= 1

    // Use the combo-specific calculation function
    combo.total = calculateComboTotal(combo)

    persistCartInLocalStorage()
  }

  function extractDefaults(comboItems: ComboItemType[]): unknown[] {
    const defaults: unknown[] = []

    log.debug('combo items', comboItems)

    comboItems.forEach((group) => {
      if (group.options && Array.isArray(group.options)) {
        group.options.forEach((option: any) => {
          if (option.default) {
            const defaultOption: any = {
              group_name: group.group_name,
              product_name: option.product_name,
              product_id: option.product_id,
              quantity: option.quantity,
              price_variation: option.price_variation,
            }

            defaults.push(defaultOption)
          }
        })
      }
    })

    return defaults
  }

  function openComboModal(combo: any): void {
    comboModal.value = true

    comboStaged.value = {
      ...deepClone(combo),
    }

    comboStaged.value.quantity = 1
    const { generate } = useUUID()
    comboStaged.value.cart_uuid = generate()
    comboStaged.value.combo_id = combo.id // Store combo ID for editing later
    comboStaged.value.defaults = extractDefaults(combo.items)
  }

  // Helper function to set default modifiers for a product
  function setDefaultModifiersForProduct(productModifiers: any[], selectedModifiers: any = {}) {
    if (!productModifiers?.length) return selectedModifiers

    productModifiers.forEach((modifier: any) => {
      // First check for default_option array
      if (modifier.default_option?.length > 0) {
        selectedModifiers[modifier.id] = modifier.default_option.map((opt: any) => ({
          id: opt.id,
          name: opt.name,
          price_modifier: opt.price_change || 0,
          quantity: 1,
        }))
      }
      // Also check for is_default flag on individual options
      else if (modifier.options?.length > 0) {
        const defaultOptions = modifier.options.filter((opt: any) => opt.is_default === true)
        if (defaultOptions.length > 0) {
          // For single selection modifiers, take only the first default
          if (!modifier.is_multiple && defaultOptions.length > 0) {
            selectedModifiers[modifier.id] = [{
              id: defaultOptions[0].id,
              name: defaultOptions[0].name,
              price_modifier: defaultOptions[0].price_change || 0,
              quantity: 1,
            }]
          }
          // For multiple selection modifiers, include all defaults
          else {
            selectedModifiers[modifier.id] = defaultOptions.map((opt: any) => ({
              id: opt.id,
              name: opt.name,
              price_modifier: opt.price_change || 0,
              quantity: 1,
            }))
          }
        }
      }
    })

    return selectedModifiers
  }

  function initializeCustomComboDefaults(): void {
    if (!comboCustomOptions.value?.items) return

    comboCustomOptions.value.items.forEach((group: any) => {
      // Find default option in each group
      const defaultOption = group.options.find((option: any) => option.default)

      if (defaultOption) {
        // Set the default option as selected for this group
        group.selected = {
          product_name: defaultOption.product_name,
          product_id: defaultOption.product_id,
          price_variation: defaultOption.price_variation,
          quantity: defaultOption.quantity,
          selected_modifiers: {},
        }

        // Set default modifiers if product has them
        group.selected.selected_modifiers = setDefaultModifiersForProduct(
          defaultOption.product_modifiers,
          group.selected.selected_modifiers,
        )
      }
    })
  }

  function prepareCustomCombo() {
    const customComboPrep = {
      cart_uuid: comboCustomOptions.value?.cart_uuid,
      name: comboStaged.value?.name,
      price: comboCustomOptions.value?.price ?? 0,
      quantity: comboStaged.value.quantity ?? 1,
      items: comboCustomOptions.value?.selected ?? [],
      total: comboCustomOptions.value?.total ?? 0,
      type: comboCustomOptions.value?.type || 'CUSTOM_COMBO',
      combo_id: comboStaged.value?.combo_id, // Include combo ID for editing
    }

    // Check if we're updating an existing combo or adding a new one
    if (stageComboInCart.value && comboCustomOptions.value?.cart_uuid) {
      updateCustomComboInCart(JSON.parse(JSON.stringify(customComboPrep)))
    }
    else {
      addCustomComboToCart(JSON.parse(JSON.stringify(customComboPrep)))
    }
  }

  function updateCustomComboInCart(updatedCombo: any): void {
    const index = cart.value.findIndex(item => item.cart_uuid === updatedCombo.cart_uuid)

    if (index !== -1) {
      // Replace the existing combo with the updated version
      const normalizedCombo: CartItem = {
        cart_uuid: updatedCombo.cart_uuid,
        name: updatedCombo.name,
        type: updatedCombo.type || 'CUSTOM_COMBO',
        base_price: updatedCombo.price || 0,
        quantity: updatedCombo.quantity || 1,
        // Don't set total - let calculateCartItemTotal handle it
        items: updatedCombo.items
          ? updatedCombo.items.map((item: any, itemIndex: number): NormalizedCartItem => ({
              id: `item_${itemIndex}`,
              product_id: item.product_id || 0,
              product_name: item.product_name || '',
              quantity: item.quantity || 1,
              base_price: 0,
              price_variation: item.price_variation || 0,
              group: item.group_id
                ? {
                    id: item.group_id,
                    name: item.group_name || '',
                    required: true,
                  }
                : null,
              selected_modifiers: item.selected_modifiers || {},
            }))
          : [],
      }

      // Calculate the correct total
      normalizedCombo.total = calculateCartItemTotal(normalizedCombo)

      cart.value[index] = normalizedCombo
      persistCartInLocalStorage()

      // Clear edit state by removing cart_uuid from staged combo
      if (comboStaged.value?.cart_uuid) {
        comboStaged.value = { ...comboStaged.value }
        delete comboStaged.value.cart_uuid
      }
      useSuccessToast('Combo updated successfully!')
    }
    else {
      useErrorToast('Combo not found in cart')
    }
  }

  async function customizeCombo() {
    comboCustomized.value = true
    comboCustomOptions.value = {
      type: 'CUSTOM_COMBO',
      items: comboStaged.value?.items ?? [],
      price: comboStaged.value?.price ?? 0,
      defaults: comboStaged.value?.defaults ?? [],
      cart_uuid: comboStaged.value?.cart_uuid ?? null,
    }

    // Auto-select default products and their modifiers
    if (comboCustomOptions.value.items && Array.isArray(comboCustomOptions.value.items)) {
      // Create a new array to ensure reactivity
      comboCustomOptions.value.items = comboCustomOptions.value.items.map((group: any) => {
        // Find default product in this group
        const defaultProduct = group.options?.find((opt: any) => opt.default)
        if (defaultProduct) {
          // Always auto-select the default product (overwrite any existing selection)
          group.selected = {
            group_id: group.group_id,
            product_id: defaultProduct.product_id,
            product_name: defaultProduct.product_name,
            quantity: defaultProduct.quantity || 1,
            price_variation: defaultProduct.price_variation || 0,
          }
        }
        return group // Return the modified group
      })
    }
  }

  async function openCombo(combo: any): Promise<void> {
    // Initialize the combo modal
    openComboModal(combo)

    const { $dialog } = useNuxtApp()

    $dialog.open(PublicStoreCombosModal, {
      props: {
        header: headerTitle.value,
        ...useDynamicDialogSettings,
        pt: dialogStyle,
        modal: true,
        position: 'top',
        style: { width: '95vw' },
        breakpoints: {
          '1199px': '90vw',
          '575px': '90vw',
        },
      },
      onClose: () => {
        resetComboData()
      },
    })
  }

  function resetComboData(): void {
    comboStaged.value = {}
    comboCustomized.value = false
    comboCustomOptions.value = null
  }

  function toggleEditButtons(): void {
    editButtons.value = !editButtons.value
    persistPreferencesInLocalStorage()
  }

  function clearSearch(): void {
    searchTerm.value = ''
  }

  // ========== Order Editing Functions ==========

  // Transform cart data from database structure back to frontend structure
  function transformCartFromDatabase(databaseCart: any[]): any[] {
    return databaseCart.map((item) => {
      // Transform combo/custom combo items
      if (item.type === 'COMBO' || item.type === 'CUSTOM_COMBO') {
        return {
          ...item,
          items: item.items?.map((subItem: any) => ({
            ...subItem,
            // Convert database modifiers array back to unified modifiers structure
            modifiers: subItem.modifiers?.length > 0
              ? subItem.modifiers.map((modifier: any) => ({
                  group_id: modifier.group_id,
                  group_name: modifier.group_name || '',
                  selected: modifier.selected || [],
                }))
              : [],
          })),
        }
      }

      // Transform product items - handle new unified structure
      if (item.type === 'PRODUCT') {
        // Check if modifiers are in product_data (new structure)
        if (item.product_data?.modifiers?.length > 0) {
          return {
            ...item,
            // Move modifiers from product_data to cart item level (new unified structure)
            modifiers: item.product_data.modifiers.map((modifier: any) => ({
              group_id: modifier.group_id,
              group_name: modifier.group_name || '',
              selected: modifier.selected || [],
            })),
            product_data: {
              ...item.product_data,
              // Keep modifiers in product_data for display components
              modifiers: item.product_data.modifiers,
            },
          }
        }

        // Handle legacy structure in item.items
        if (item.items?.length > 0) {
          return {
            ...item,
            items: item.items.map((subItem: any) => ({
              ...subItem,
              // Convert database modifiers array back to unified modifiers structure
              modifiers: subItem.modifiers?.length > 0
                ? subItem.modifiers.map((modifier: any) => ({
                    group_id: modifier.group_id,
                    group_name: modifier.group_name || '',
                    selected: modifier.selected || [],
                  }))
                : [],
            })),
          }
        }
      }

      return item
    })
  }

  async function loadOrderForEditing(orderId: string): Promise<void> {
    try {
      const order = await get(`/api/orders/${orderId}`)

      if (!order?.cart) {
        throw new Error('Order does not have cart data')
      }

      // Clear current state
      clearCart()
      resetComboData()
      resetProductStaged()

      // Transform cart data from database structure to frontend structure
      cart.value = transformCartFromDatabase(order.cart)
      editingOrderId.value = order.id
      isEditingOrder.value = true

      // Load customer details if available
      if (order.client_details) {
        details.value = { ...initialDetailsState, ...order.client_details }
      }

      // Open cart for editing
      openCart.value = true

      log.info('Order loaded for editing:', order.id)
    }
    catch (error) {
      log.error('Failed to load order:', error)
      throw error
    }
  }

  function cancelOrderEdit(): void {
    clearCart()
    editingOrderId.value = null
    isEditingOrder.value = false
    openCart.value = false
  }

  function isCurrentlyEditingOrder(): boolean {
    return isEditingOrder.value && editingOrderId.value !== null
  }

  async function initStore(buuidParam?: string): Promise<void> {
    if (import.meta.client) {
      readCartFromLocalStorage()

      // Initialize core data (products, categories, combos) if we have businessId
      const buuid = buuidParam || businessId.value
      if (buuid) {
        await initializeStoreData()
      }
    }
  }

  // ========== Store Data Management ==========

  const isLoadingData = ref(false)

  // Check if we have the core data loaded
  const hasData = computed(() => {
    return products.value.length > 0 || categories.value.length > 0 || combos.value.length > 0
  })

  // Initialize all store data in one go
  async function initializeStoreData(forceRefresh = false): Promise<void> {
    // businessId comes from storeToRefs(businessStore), already reactive
    const buuid = businessId.value

    if (!buuid) {
      log.warn('No business ID available for data initialization')
      return
    }

    // Skip if we already have data and not forcing refresh
    if (hasData.value && !forceRefresh) {
      log.info('Store data already loaded, skipping initialization')
      // Still ensure payment methods and discounts are ready (each has its own staleness guard)
      await Promise.all([paymentMethodsStore.fetchItems(), discountsStore.fetchDiscounts()])
      return
    }

    isLoadingData.value = true

    try {
      // Fetch all data in parallel for better performance — payment methods included
      const [categoriesData, productsData, combosData] = await Promise.all([
        get(`/api/business/${buuid}/categories`),
        get(`/api/business/${buuid}/products?version=v2`),
        get(`/api/business/${buuid}/combos`),
        paymentMethodsStore.fetchItems(),
        discountsStore.fetchDiscounts(),
      ])

      // Update store state
      categories.value = categoriesData || []
      products.value = productsData || []
      combos.value = combosData || []

      log.info('✅ Store data initialized successfully')
    }
    catch (error) {
      log.error('Failed to initialize store data:', error)

      // If offline, we'll rely on persisted data
      if (!navigator.onLine) {
        log.info('📱 Offline - using persisted data if available')
      }
    }
    finally {
      isLoadingData.value = false
    }
  }

  // Clear all cached data and refresh
  async function clearCache(): Promise<void> {
    log.info('🔄 Clearing cache and refreshing data...')

    // Clear the data
    products.value = []
    categories.value = []
    combos.value = []

    // Refresh all data
    await initializeStoreData(true)
  }

  // ========== Watchers ==========

  // Sync selected category with cookie
  watch(selectedCategoryId, (newValue) => {
    selectedCategoryCookie.value = newValue
  })

  watch(comboCustomized, () => {
    initializeCustomComboDefaults()
  })

  // Reset cash calculation when cart total changes
  watch(cartTotal, () => {
    resetCashCalculation()
  })

  // Helper functions
  const calculatePriceVariations = (selected: ComboItem[]): number => {
    return selected.reduce((acc, item) => {
      if (item && typeof item.price_variation !== 'undefined') {
        return acc + Number(item.price_variation)
      }
      return acc
    }, 0)
  }

  const calculateExtras = (selected: ComboItem[]): number => {
    return selected.reduce((acc, item) => {
      let itemTotal = 0

      // Unified modifiers (now always arrays)
      if (item?.selected_modifiers) {
        Object.values(item.selected_modifiers).forEach((selections) => {
          // Always expect arrays now
          if (Array.isArray(selections)) {
            selections.forEach((selection) => {
              const modifierPrice = selection.price_modifier || 0
              // For CUSTOM_COMBO, multiply by item quantity
              const isCustomCombo = comboCustomOptions.value?.type === 'CUSTOM_COMBO'
              itemTotal += isCustomCombo && item.quantity ? modifierPrice * item.quantity : modifierPrice
            })
          }
        })
      }

      return acc + itemTotal
    }, 0)
  }

  // Computed property
  const processedComboOptions = computed(() => {
    const items = comboCustomOptions.value?.items

    if (!items) {
      return {
        selected: [],
        itemsNeeded: 0,
        total: 0,
      }
    }

    try {
      // Deep clone selected items to ensure modifiers are preserved
      const selected = items.map(item => item.selected ? JSON.parse(JSON.stringify(item.selected)) : null).filter(Boolean)

      const priceVariations = calculatePriceVariations(selected)
      const extras = calculateExtras(selected)

      // Calculate items needed
      const defaultsCount = comboCustomOptions.value?.defaults?.length ?? 0
      const selectedCount = selected.filter(Boolean).length
      const itemsNeeded = defaultsCount - selectedCount

      // Calculate total price factoring in quantity
      const basePrice = comboCustomOptions.value?.price ?? 0
      const quantity = comboStaged.value?.quantity ?? 1
      const total = (priceVariations + basePrice + extras) * quantity

      return {
        selected,
        itemsNeeded,
        total,
      }
    }
    catch (error) {
      log.error('Error processing custom combo options:', error)
      return {
        selected: [],
        itemsNeeded: 0,
        total: 0,
      }
    }
  })

  // Update original state if needed
  watch(processedComboOptions, (newValue) => {
    if (comboCustomOptions.value) {
      comboCustomOptions.value.selected = newValue.selected
      comboCustomOptions.value.itemsNeeded = newValue.itemsNeeded
      comboCustomOptions.value.total = newValue.total
    }
  }, { immediate: true })

  return {
    // Business state (from business store)
    businessId,
    businessInfo,
    products,
    combos,
    categories,
    cart,
    openCart,
    productModal,
    productStaged,
    discounts,
    selectedDiscount,
    comboModal,
    comboStaged,
    comboCustomized,
    comboCustomOptions,
    details,
    initialDetailsState,
    editButtons,
    compactViewEnabled,
    selectedPaymentMethod,
    isUpdatingOrder,

    // Counter mode cash calculation
    cashReceived,

    // Order editing state
    editingOrderId,
    isEditingOrder,

    // Category filtering state
    selectedCategoryId,

    // Search filtering state
    searchTerm,

    // Data management state
    isLoadingData,
    hasData,

    // Computed
    itemsInCart,
    cartTotal,
    hasClientDetails,
    stageProductInCart,
    productStagedTotal,
    stageComboInCart,
    comboStagedTotal,
    paymentMethodSurchargePercentage,
    calculatedChange,
    headerTitle,
    filteredProducts,
    availableCategories,

    // Methods
    initStore,
    initializeStoreData,
    clearCache,
    reset,
    persistDetailsInLocalStorage,
    clearDetails,
    clearCart,
    setCashReceived,
    resetCashCalculation,

    // Product Methods
    stageProduct,
    // setProductOptionSelected: REMOVED - deprecated
    addProductToCart,
    quickAddToCart,
    updateProductInCart,
    resetProductStaged,
    removeProductFromCart,
    editProductFromCart,
    increaseProductQuantity,
    decreaseProductQuantity,
    addOneToCart,
    removeOneFromCart,

    // Combo Methods
    addComboToCart,
    addCustomComboToCart,
    addCustomItemToCart,
    editComboFromCart,
    updateCustomComboInCart,
    increaseComboQuantityStaged,
    decreaseComboQuantityStaged,
    increaseComboQuantityCart,
    decreaseComboQuantityCart,
    increaseCustomComboQuantityCart,
    decreaseCustomComboQuantityCart,
    openCombo,
    prepareCustomCombo,
    customizeCombo,

    // UI Methods
    toggleEditButtons,
    clearSearch,

    // Order editing methods
    loadOrderForEditing,
    cancelOrderEdit,
    transformCartFromDatabase,
    isCurrentlyEditingOrder,

    // Unified Structure Helper Functions
    mapProductModifiersToCartModifiers,
    calculateCartItemTotal,

    // Validation Functions
    validateRequiredModifiers() {
      if (!comboCustomOptions.value?.items) return true

      for (const group of comboCustomOptions.value.items) {
        if (!group.selected) continue

        const selectedItem = group.selected

        // Find the original product option to get its modifiers
        const originalOption = group.options?.find(opt => opt.product_id === selectedItem.product_id)
        if (!originalOption?.product_modifiers) continue

        // Check if all required modifiers have selections
        for (const modifier of originalOption.product_modifiers) {
          if (modifier.is_required) {
            const hasSelection = selectedItem.selected_modifiers?.[modifier.id]
              && Array.isArray(selectedItem.selected_modifiers[modifier.id])
              && selectedItem.selected_modifiers[modifier.id].length > 0

            if (!hasSelection) {
              return false
            }
          }
        }
      }

      return true
    },
  }
}, {
  persist: typeof window !== 'undefined'
    ? {
        key: 'commerce-store',
        storage: localStorage,
        paths: [
          'products',
          'categories',
          'combos',
          'cart',
          'editButtons',
          'compactViewEnabled',
          'selectedDiscount',
          'selectedPaymentMethod',
          'details',
          'selectedCategoryId',
        ],
        // Validate business context after hydration
        afterRestore: (ctx) => {
          const currentBusinessId = useBusinessStore().businessId

          // If commerce store has data but businessId doesn't match, clear it
          if (ctx.store.cart?.length > 0 && currentBusinessId) {
            const log = useLogger('store:commerce:restore')
            log.info('🔍 Validating commerce data against current business:', currentBusinessId)

            // Since we removed businessId from persist, we can't validate
            // Instead, rely on login flow clearing commerce-store
            // This hook ensures we log when restoration happens
          }
        },
      }
    : false,
})
